import { useEffect, useRef, useState } from 'react'
import {
  animateEllipsis,
  animateFoundSteps,
  animateMagnifyingGlass,
  animateSparklesPopIn,
  animateTransactionCounter,
} from './transactionReviewAnimations'

/** Floor for the already-done case: a faithful animation of zero elapsed work reads as broken. */
const MINIMUM_RUN_MS = 1800

interface AnimationInput {
  /** Transactions settled so far, the count-up's real target. */
  settled: number
  /** Total for this batch; 0 while importing, when it isn't known yet. */
  total: number
  /** True once the whole pipeline has finished. */
  isSettled: boolean
}

export const useTransactionReviewAnimation = ({
  settled,
  total,
  isSettled,
}: AnimationInput) => {
  const magnifyingGlassRef = useRef<SVGGElement>(null)
  const ellipsisRef = useRef<HTMLSpanElement>(null)
  const foundStepsRef = useRef<HTMLDivElement>(null)
  const sparkleOneRef = useRef<SVGGElement>(null)
  const sparkleTwoRef = useRef<SVGGElement>(null)
  const magnifyingGlassTweenRef =
    useRef<ReturnType<typeof animateMagnifyingGlass>>(undefined)
  const countRef = useRef(0)
  const mountedAtRef = useRef(Date.now())
  const [count, setCount] = useState(0)
  const [isComplete, setIsComplete] = useState(false)

  useEffect(() => {
    const tween = animateMagnifyingGlass(magnifyingGlassRef.current)
    magnifyingGlassTweenRef.current = tween
    return () => {
      tween?.kill()
    }
  }, [])

  useEffect(() => {
    if (isComplete) {
      magnifyingGlassTweenRef.current?.kill()
    }
  }, [isComplete])

  useEffect(() => {
    const timeline = animateEllipsis(ellipsisRef.current)
    return () => {
      timeline?.kill()
    }
  }, [])

  // Re-runs whenever more work lands, resuming from the current value so the number only
  // ever moves forward. When the pipeline is already finished at mount, the remaining
  // minimum duration is spent counting rather than skipped.
  useEffect(() => {
    if (settled <= countRef.current) return undefined

    const elapsed = Date.now() - mountedAtRef.current
    const duration = isSettled
      ? Math.max((MINIMUM_RUN_MS - elapsed) / 1000, 0.4)
      : 1.2

    const tween = animateTransactionCounter(
      (value) => {
        countRef.current = value
        setCount(value)
      },
      settled,
      countRef.current,
      duration
    )

    return () => {
      tween.kill()
    }
  }, [settled, isSettled])

  // The found-steps reveal waits for both the real work and the floor, so a returning user
  // still sees the sorting moment instead of a flash.
  useEffect(() => {
    if (!isSettled || count < settled) return undefined

    const remaining = Math.max(
      MINIMUM_RUN_MS - (Date.now() - mountedAtRef.current),
      0
    )
    const timer = setTimeout(() => setIsComplete(true), remaining)

    return () => {
      clearTimeout(timer)
    }
  }, [isSettled, count, settled])

  useEffect(() => {
    if (!isComplete) return undefined
    const tween = animateFoundSteps(foundStepsRef.current)
    return () => {
      tween?.kill()
    }
  }, [isComplete])

  useEffect(() => {
    if (!isComplete) return undefined
    const tween = animateSparklesPopIn([
      sparkleOneRef.current,
      sparkleTwoRef.current,
    ])
    return () => {
      tween?.kill()
    }
  }, [isComplete])

  // Null while the total is unknown: Plaid's cursor sync doesn't announce a count, so a
  // ratio during import would move backwards as the denominator grows.
  const progressPercent =
    total > 0 ? Math.min((count / total) * 100, 100) : null

  return {
    magnifyingGlassRef,
    ellipsisRef,
    foundStepsRef,
    sparkleOneRef,
    sparkleTwoRef,
    count,
    isComplete,
    progressPercent,
  }
}
